import { useEffect, useRef, useState } from "react";
import { useParams, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { ArticleMasthead } from "@/components/blog/article-masthead";
import { useSEO } from "@/lib/seo-utils";
import type { LegalDocument } from "@/components/legal/house-rules-display";
import { ShareWaiverLink, StayReturnLink, StickySignBar } from "@/components/legal/share-waiver-link";
import { GUEST_RELEASE_REQUIRED_FROM_LABEL } from "@shared/guest-release";

/**
 * Per-adult signing page for the Guest Release of Liability, Assumption of
 * Risk and Indemnity Agreement.
 *
 * Every adult signs for themselves; the entry codes wait until everyone on
 * the booking has. Built the way a release has the best chance of being
 * enforced: the whole agreement is shown and must be scrolled to the end
 * before signing unlocks, the risks and the release are separate
 * affirmations, and the signature must be the signer's own name.
 *
 * Children are added by a parent or guardian. Hawaii does not enforce a
 * parent's pre-injury release of a child's own claims, so the parent's
 * affirmation is about supervision and their own claims, and says so.
 */
export default function ReleasePage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const search = useSearch();
  const guestToken = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("token");

  type ReleaseContext = {
    document: LegalDocument;
    releaseUrl: string | null;
    signed: number;
    required: number;
    remaining: number;
    complete: boolean;
    /** The stay checks in before the Guest Release applies. */
    exempt?: boolean;
  };
  const contextUrl = `/api/legal/release-context/${bookingId}${guestToken ? `?token=${encodeURIComponent(guestToken)}` : ""}`;
  const { data: context, isLoading, isError, error: contextError, refetch: refetchContext } = useQuery<ReleaseContext>({
    queryKey: [contextUrl],
    enabled: Boolean(bookingId),
    retry: false,
  });
  const contextStatus = contextError instanceof Error ? parseInt(contextError.message, 10) : NaN;
  const bookingMissing = contextStatus === 404;
  const doc = context?.document;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [children, setChildren] = useState<string[]>([]);
  const [childDraft, setChildDraft] = useState("");
  const [read, setRead] = useState(false);
  const [riskAck, setRiskAck] = useState(false);
  const [releaseAck, setReleaseAck] = useState(false);
  const [guardianAck, setGuardianAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ at: string; version: number } | null>(null);
  const [adultRecorded, setAdultRecorded] = useState(false);
  const [childrenRecorded, setChildrenRecorded] = useState<string[]>([]);

  const documentRef = useRef<HTMLDivElement>(null);

  function resetConsent() {
    if (documentRef.current) documentRef.current.scrollTop = 0;
    setRead(false);
    setRiskAck(false);
    setReleaseAck(false);
    setGuardianAck(false);
  }

  // Consent applies only to the exact text shown for this exact link.
  useEffect(() => {
    resetConsent();
    setDone(null);
    setAdultRecorded(false);
    setChildrenRecorded([]);
  }, [bookingId, guestToken, doc?.contentHash]);

  useEffect(() => {
    setName("");
    setEmail("");
    setSignature("");
    setChildren([]);
    setChildDraft("");
    setError(null);
  }, [bookingId, guestToken]);

  const seoHead = useSEO({
    pageType: "waiver",
    title: "Guest Release | Vacation Rental Oahu",
    description: "Sign the guest release for your stay.",
    robots: "noindex, nofollow",
    referrer: "no-referrer",
  });

  // Signing unlocks only once the whole agreement has been scrolled to the end.
  useEffect(() => {
    const el = documentRef.current;
    if (!el || read) return;
    const check = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setRead(true);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [doc, read]);

  const signatureMatches =
    signature.trim().length >= 2 &&
    signature.trim().toLowerCase() === name.trim().toLowerCase();
  const namedChildren = children.map((c) => c.trim()).filter((c) => c.length >= 2);
  const childrenOk = namedChildren.length === 0 || guardianAck;
  const emailOk = /\S+@\S+\.\S+/.test(email);
  const ready =
    Boolean(context && doc) &&
    read && riskAck && releaseAck && childrenOk &&
    name.trim().length >= 2 && emailOk && signatureMatches && !submitting;
  const disabledReason = !context
    ? "Open an authorized link to sign."
    : !read
      ? "Read the agreement to the end to unlock signing."
      : name.trim().length < 2
        ? "Enter your full name."
        : !emailOk
          ? "Enter a valid email address."
          : !riskAck || !releaseAck || !childrenOk
            ? "Complete the required acknowledgements."
            : !signatureMatches
              ? "Type your full name exactly as shown above."
              : submitting
                ? "Your signature is being recorded."
                : null;

  function addChild() {
    const v = childDraft.trim();
    if (v.length < 2) return;
    if (children.some((c) => c.trim().toLocaleLowerCase() === v.toLocaleLowerCase())) {
      setError("That child is already listed.");
      return;
    }
    setError(null);
    setChildren((prev) => [...prev, v]);
    setChildDraft("");
  }

  async function submit() {
    if (!context || !doc) {
      setError("This secure link is not authorized. Open the newest booking email for a fresh link.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const recorded: string[] = [];
    try {
      const base = {
        slug: "guest_release",
        bookingId: bookingId || null,
        method: "emailed_link",
        signatureName: signature.trim(),
        expectedVersionNumber: doc.versionNumber,
        expectedContentHash: doc.contentHash,
      };
      const tokenQuery = guestToken ? `?token=${encodeURIComponent(guestToken)}` : "";
      const accept = async (payload: Record<string, unknown>) => {
        const res = await fetch(`/api/legal/accept${tokenQuery}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const err = new Error(body.message || "We could not record this signature.") as Error & { status?: number; code?: string };
          err.status = res.status;
          err.code = body.error_code;
          throw err;
        }
        return body;
      };
      let body: any = null;
      if (!adultRecorded) {
        body = await accept({
          ...base,
          guestEmail: email.trim(),
          guestName: name.trim(),
          signerRole: "adult_signer",
          riskDisclosureAck: riskAck,
          releaseAck,
        });
        setAdultRecorded(true);
        recorded.push(name.trim());
      }
      for (const child of namedChildren) {
        const key = child.toLocaleLowerCase();
        if (childrenRecorded.includes(key)) continue;
        const childBody = await accept({
          ...base,
          guestEmail: email.trim(),
          guestName: child,
          childName: child,
          guardianEmail: email.trim(),
          guardianName: name.trim(),
          signerRole: "guardian",
          partyConfirmed: guardianAck,
          riskDisclosureAck: guardianAck,
          releaseAck: guardianAck,
        });
        body = body ?? childBody;
        recorded.push(child);
        setChildrenRecorded((current) => current.includes(key) ? current : [...current, key]);
      }
      if (!body) body = { acceptedAt: new Date().toISOString(), versionNumber: doc.versionNumber };
      setDone({ at: body.acceptedAt, version: body.versionNumber });
      await refetchContext();
    } catch (e) {
      await refetchContext();
      const err = e as Error & { status?: number; code?: string };
      if (err.code === "VERSION_CHANGED") {
        resetConsent();
        setAdultRecorded(false);
        setChildrenRecorded([]);
        setError("The agreement was updated while you were signing. We loaded the current version. Please read it again, confirm the acknowledgements, and sign.");
        setSubmitting(false);
        return;
      }
      const reason = err instanceof Error ? err.message : "We could not record the signature.";
      setError(
        recorded.length
          ? `${recorded.join(", ")} ${recorded.length === 1 ? "was" : "were"} recorded, but the remaining signature was not. Your details are still here. Please try again. ${reason}`
          : reason,
      );
    }
    setSubmitting(false);
  }

  function renderBody(body: string) {
    return body.split("\n").filter(Boolean).map((line, i) =>
      line.startsWith("- ") ? (
        <li key={i} className="flex gap-3 font-sans text-[15px] leading-relaxed text-ink-soft">
          <span className="shrink-0 text-driftwood">–</span>
          <span>{line.slice(2)}</span>
        </li>
      ) : (
        <p key={i} className="mb-3 font-sans text-[15px] leading-relaxed text-ink-soft">{line}</p>
      ),
    );
  }

  const waiverUrl = bookingId
    ? `/waiver/${encodeURIComponent(bookingId)}${guestToken ? `?token=${encodeURIComponent(guestToken)}` : ""}`
    : null;
  const shareUrl = context?.releaseUrl ?? null;
  const checkbox = "mt-0.5 h-5 w-5 shrink-0 accent-[#17311C]";
  const inputCls = "w-full border border-sand-deep bg-shell min-h-[48px] px-4 py-3 font-sans text-[16px] text-ink focus:border-forest focus:outline-none";
  const labelCls = "mb-2 block font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood";

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main id="main-content">
          <ArticleMasthead
            eyebrow="Before you arrive"
            title="Guest Release"
            deck="Every adult on the booking signs this online, for themselves, and parents add their children. Your entry codes are released once everyone has signed. It takes a couple of minutes."
          />

          <div className="container mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
            {isLoading && <p className="font-sans text-ink-soft">Loading the agreement…</p>}

            {isError && (
              <div className="border border-sand-deep bg-sand/50 p-6" data-testid="release-link-error">
                <h2 className="mb-2 font-serif text-luxury-xl text-forest">
                  {bookingMissing ? "We could not find a booking for this link." : "This link has expired."}
                </h2>
                <p className="font-sans text-[15px] leading-relaxed text-ink-soft" role="alert">
                  Open the latest booking email and use the link in it, ask the person who booked to share it from their stay page, or contact your host.
                </p>
              </div>
            )}

            {done && (
              <div className="rounded-2xl border border-sand-deep bg-sand p-6 text-center sm:p-8" data-testid="release-signed">
                <h2 className="mb-3 font-serif text-luxury-2xl font-normal text-forest">Signed. Thank you.</h2>
                <p className="mx-auto max-w-[46ch] font-sans text-[15px] leading-relaxed text-ink-soft">
                  Recorded {new Date(done.at).toLocaleString("en-US")} against version {done.version} of the
                  agreement. A copy of exactly what you signed is kept and is available to you on request.
                </p>
                <p className="mx-auto mt-5 max-w-[46ch] font-sans text-[14px] leading-relaxed text-driftwood">
                  {context?.complete
                    ? "That was the last signature. Your entry codes are released on your stay page and by email the day before arrival."
                    : context
                      ? `${context.signed} of ${context.required} have now signed. Everyone else on the booking signs for themselves, so please pass this link on. The entry codes are released once everyone has signed.`
                      : "Everyone else on the booking signs for themselves, so please pass this link on."}
                </p>
                {!context?.complete && shareUrl && (
                  <ShareWaiverLink url={shareUrl} documentName="guest release" className="mx-auto mt-6 max-w-md text-left" />
                )}
                <StayReturnLink bookingId={bookingId} className="mt-6" />
                {waiverUrl && (
                  <p className="mx-auto mt-6 max-w-[46ch] font-sans text-[14px] leading-relaxed text-ink-soft">
                    Planning to use the kayaks, paddleboards or surfboards?{" "}
                    <a href={waiverUrl} className="text-forest underline" data-testid="release-to-waiver">Sign the water activity waiver too.</a>
                  </p>
                )}
              </div>
            )}

            {context?.exempt && (
              <div className="rounded-2xl border border-sand-deep bg-sand p-6 text-center sm:p-8" data-testid="release-not-required">
                <h2 className="mb-3 font-serif text-luxury-2xl font-normal text-forest">Nothing to sign for this stay</h2>
                <p className="mx-auto max-w-[46ch] font-sans text-[15px] leading-relaxed text-ink-soft">
                  The Guest Release applies to stays from {GUEST_RELEASE_REQUIRED_FROM_LABEL}. Yours checks in before then, so there is nothing to sign and your entry codes arrive as usual.
                </p>
              </div>
            )}

            {context && !context.exempt && !done && (
              <div className="mb-10 rounded-2xl border border-sand-deep bg-sand/50 p-4 sm:p-5" data-testid="release-progress">
                <p className="font-sans text-[15px] leading-relaxed text-ink">
                  {context.complete
                    ? "Everyone on this booking has signed. The entry codes are released."
                    : `${context.signed} of ${context.required} on this booking have signed. The entry codes are released once everyone has.`}
                </p>
                {!context.complete && shareUrl && <ShareWaiverLink url={shareUrl} documentName="guest release" className="mt-4" />}
                <StayReturnLink bookingId={bookingId} className="mt-3" />
              </div>
            )}

            {doc && !context?.exempt && !done && (
              <div className="space-y-12">
                <section>
                  <h2 className="mb-2 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">{doc.title}</h2>
                  <p className="mb-4 font-sans text-[13px] uppercase tracking-[0.2em] text-driftwood">Read to the end to continue</p>
                  <div
                    ref={documentRef}
                    className="max-h-[28rem] space-y-8 overflow-y-auto overscroll-contain rounded-xl border border-sand-deep bg-white/60 p-4 sm:p-6"
                    data-testid="release-document"
                  >
                    {doc.sections.map((s) => (
                      <div key={s.anchor || s.heading}>
                        <h3 className="mb-3 font-serif text-[18px] font-normal text-forest">{s.heading}</h3>
                        <ul className="space-y-3">{renderBody(s.body)}</ul>
                      </div>
                    ))}
                  </div>
                  <p className={`mt-3 font-sans text-[13px] ${read ? "text-forest" : "text-driftwood"}`} data-testid="release-read-state">
                    {read ? "Agreement read." : "Scroll to the end of the agreement to continue."}
                  </p>
                </section>

                <section id="sign-section" className="scroll-mt-28 border-t border-sand-deep pt-10">
                  <h2 className="mb-6 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">Sign</h2>
                  <fieldset disabled={!read} className="min-w-0 space-y-6 disabled:opacity-50" data-testid="release-fieldset" aria-describedby="release-signing-status">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <label className="block">
                        <span className={labelCls}>Your full name</span>
                        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="release-name" />
                      </label>
                      <label className="block">
                        <span className={labelCls}>Your email</span>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="release-email" />
                      </label>
                    </div>

                    <div className="space-y-3">
                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand-deep bg-shell p-4 transition-colors has-[:checked]:border-forest/40 has-[:checked]:bg-sand/50">
                        <input type="checkbox" checked={riskAck} onChange={(e) => setRiskAck(e.target.checked)} className={checkbox} data-testid="release-ack-risk" />
                        <span className="font-sans text-[15px] leading-relaxed text-ink">
                          I have read the risks described in this agreement, I understand them, and I voluntarily assume all risks of the stay.
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand-deep bg-shell p-4 transition-colors has-[:checked]:border-forest/40 has-[:checked]:bg-sand/50">
                        <input type="checkbox" checked={releaseAck} onChange={(e) => setReleaseAck(e.target.checked)} className={checkbox} data-testid="release-ack-release" />
                        <span className="font-sans text-[15px] leading-relaxed text-ink">
                          I am 18 or older. I agree to the release of liability, including for the ordinary negligence of the Owner Parties, and to the indemnity, to the fullest extent Hawaii law permits. I understand I am giving up substantial legal rights.
                        </span>
                      </label>
                    </div>

                    <div className="rounded-xl border border-sand-deep bg-sand/50 p-4 sm:p-5" data-testid="release-children">
                      <p className="mb-1 font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">Children under 18 in my care</p>
                      <p className="mb-3 font-sans text-[13px] leading-relaxed text-driftwood">
                        Add each child staying with you. Each child is added by one parent or guardian.
                      </p>
                      {namedChildren.length > 0 && (
                        <ul className="mb-3 divide-y divide-sand-deep border-y border-sand-deep">
                          {children.map((c, i) => (
                            <li key={c.trim().toLocaleLowerCase()} className="flex items-center justify-between gap-3 py-2">
                              <span className="font-sans text-[15px] text-ink">{c}</span>
                              <button
                                type="button"
                                onClick={() => setChildren((prev) => prev.filter((_, j) => j !== i))}
                                className="inline-flex min-h-[44px] items-center px-2 font-sans text-[12px] uppercase tracking-[0.16em] text-driftwood hover:text-ink"
                                aria-label={`Remove ${c}`}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex gap-2">
                        <input
                          value={childDraft}
                          onChange={(e) => setChildDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChild(); } }}
                          placeholder="Child's full name"
                          className="min-w-0 flex-1 border border-sand-deep bg-shell min-h-[48px] px-4 py-3 font-sans text-[16px] text-ink focus:border-forest focus:outline-none"
                          data-testid="release-child-name"
                        />
                        <button
                          type="button"
                          onClick={addChild}
                          className="min-h-[48px] shrink-0 border border-sand-deep bg-shell px-5 py-3 font-sans text-[12px] uppercase tracking-[0.16em] text-forest transition-colors hover:bg-sand"
                          data-testid="release-child-add"
                        >
                          Add
                        </button>
                      </div>
                      {namedChildren.length > 0 && (
                        <label className="mt-4 flex cursor-pointer items-start gap-3">
                          <input type="checkbox" checked={guardianAck} onChange={(e) => setGuardianAck(e.target.checked)} className={checkbox} data-testid="release-ack-guardian" />
                          <span className="font-sans text-[15px] leading-relaxed text-ink">
                            I am the parent or legal guardian of the children named here. I have read this agreement on their behalf, I will supervise them at all times, and I agree to the Children section, including releasing my own claims and the indemnity, to the extent Hawaii law allows.
                          </span>
                        </label>
                      )}
                    </div>

                    <label className="block">
                      <span className={labelCls}>Type your full name to sign</span>
                      <input
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        placeholder={name || "Your full name"}
                        className="w-full border border-sand-deep bg-shell px-4 py-3 font-serif text-[20px] text-ink focus:border-forest focus:outline-none"
                        data-testid="release-signature"
                      />
                      {signature.trim().length > 0 && !signatureMatches && (
                        <span className="mt-2 block font-sans text-[13px] text-driftwood">Your signature must match the name above.</span>
                      )}
                    </label>
                  </fieldset>

                  {error && <p className="mt-6 font-sans text-[14px] text-red-700" role="alert" data-testid="release-error">{error}</p>}
                  {!ready && disabledReason && (
                    <p id="release-signing-status" className="mt-6 font-sans text-[13px] leading-relaxed text-driftwood" role="status">{disabledReason}</p>
                  )}

                  <button
                    type="button"
                    disabled={!ready}
                    onClick={submit}
                    className="mt-8 inline-flex min-h-[52px] w-full items-center justify-center bg-sun px-8 py-4 font-sans text-[16px] tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover disabled:cursor-not-allowed disabled:bg-driftwood/40 disabled:text-shell/70 sm:w-auto"
                    data-testid="release-submit"
                  >
                    {submitting ? "Recording…" : "Sign the Guest Release"}
                  </button>

                  <p className="mt-5 font-sans text-[13px] leading-relaxed text-driftwood">
                    We record the date and time, your signature, and the exact text you signed.
                  </p>
                </section>
              </div>
            )}
          </div>
        </main>
        {doc && !context?.exempt && !done && (
          <StickySignBar
            targetId="sign-section"
            title={read ? "Ready to sign" : "Sign the Guest Release"}
            detail={read ? "Your details and signature are below." : "Read the agreement to the end, then sign."}
            cta="Go to signing"
          />
        )}
        <Footer />
      </div>
    </>
  );
}
